import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

const HeroSection: React.FC = () => {
  const [scrollY, setScrollY] = useState(0);
  const [seatCount, setSeatCount] = useState<number>(1);
  const [selectedTools, setSelectedTools] = useState<Record<string, boolean>>({});

  const toolOptions = useMemo(
    () => [
      { id: 'crm', label: 'CRM (HubSpot / Salesforce)', monthlyCost: 50 },
      { id: 'pm', label: 'Project + Automation (Asana / Monday / Zapier)', monthlyCost: 22 },
      { id: 'content', label: 'Content + Copy (Jasper / SurferSEO)', monthlyCost: 29 },
      { id: 'hr', label: 'HR & Payroll (BambooHR / Gusto)', monthlyCost: 18 },
      { id: 'cold-email', label: 'Cold Email (Instantly / Lemlist)', monthlyCost: 29 },
      { id: 'seo', label: 'SEO Suite (Ahrefs / SEMrush)', monthlyCost: 65 },
      { id: 'social', label: 'Social (Buffer / Hootsuite)', monthlyCost: 15 },
      { id: 'recruiting', label: 'Recruiting (Greenhouse / Lever)', monthlyCost: 20 },
      { id: 'branding', label: 'Branding (Looka / 99designs)', monthlyCost: 25 },
      { id: 'spreadsheets', label: 'Finance Ops (Excel / Google Sheets)', monthlyCost: 9 }
    ],
    []
  );

  const toggleTool = (toolId: string) => {
    setSelectedTools((prev) => ({
      ...prev,
      [toolId]: !prev[toolId]
    }));
  };

  const formatCurrency = (value: number, maximumFractionDigits = 0) =>
    new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits
    }).format(value);

  const sanitizedSeatCount = Math.min(Math.max(1, Math.floor(seatCount || 1)), 50);
  const currentMonthlySpend = useMemo(() => {
    const perSeat = toolOptions.reduce(
      (sum, tool) => sum + (selectedTools[tool.id] ? tool.monthlyCost : 0),
      0
    );
    return perSeat * sanitizedSeatCount;
  }, [selectedTools, toolOptions, sanitizedSeatCount]);

  const currentAnnualSpend = currentMonthlySpend * 12;
  const isTeamPlan = sanitizedSeatCount > 1;
  const planLabel = isTeamPlan ? 'Team plan' : 'Solo plan';
  const monthlyPlanCost = isTeamPlan
    ? 49 + Math.max(0, sanitizedSeatCount - 3) * 15
    : 19;
  const annualPlanCost = isTeamPlan
    ? 490 + Math.max(0, sanitizedSeatCount - 3) * 150
    : 182.4;

  const monthlySavings = Math.max(0, currentMonthlySpend - monthlyPlanCost);
  const annualSavings = Math.max(0, currentAnnualSpend - annualPlanCost);
  const recommendAnnual = annualSavings >= annualPlanCost * 0.8 && annualSavings > 0;
  const recommendationLabel = recommendAnnual ? 'Annual' : 'Monthly';
  const recommendedSavings = recommendAnnual ? annualSavings : monthlySavings;

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative overflow-hidden bg-white">
      {/* Clean light background with subtle accent */}
      <div className="absolute inset-0">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-white to-cyan-50/20"></div>
        
        {/* Very subtle decorative elements */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
        <div className="absolute bottom-40 left-20 w-80 h-80 bg-cyan-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
      </div>
      
      {/* Content */}
      <div className="container relative z-10 mx-auto px-6 py-24 md:py-32">
        <div className="max-w-4xl mx-auto text-center">
          {/* Static, professional headline - no rotating text */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-4 text-gray-900 leading-tight">
            Run Your Entire Business With One AI Platform
          </h1>
          <p className="text-lg md:text-xl text-blue-600 font-semibold mb-6">
            From idea to revenue — 23 tools. One login. £19/month.
          </p>
        
          
          <p className="text-xl md:text-2xl text-gray-600 mb-10 leading-relaxed">
            Explanatum replaces 10+ subscriptions with one interconnected AI system that plans, builds, markets, and grows your business — with logical automation.
          </p>
          
          {/* Key benefits - clean list */}
          <div className="mb-10 flex flex-wrap justify-center gap-x-8 gap-y-3 text-base text-gray-700">
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>23 AI-powered modules</span>
            </span>
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Replace 10+ subscriptions</span>
            </span>
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>One fixed monthly price</span>
            </span>
          </div>

          {/* Savings Calculator */}
          <div className="mb-12">
            <div className="mx-auto max-w-4xl rounded-3xl border border-blue-100 bg-white/80 p-6 shadow-xl backdrop-blur">
              <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
                <div className="text-left">
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-500">
                    Savings Calculator
                  </p>
                  <h2 className="mt-2 text-2xl md:text-3xl font-bold text-slate-900">
                    See what you save by switching to Explanatum
                  </h2>
                  <p className="mt-2 text-sm text-slate-500">
                    Estimates use conservative UK pricing. Select what you currently pay for, then set your team size.
                  </p>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {toolOptions.map((tool) => (
                      <label
                        key={tool.id}
                        className={`flex cursor-pointer items-center justify-between rounded-xl border px-4 py-3 text-sm transition-all ${
                          selectedTools[tool.id]
                            ? 'border-blue-500 bg-blue-50 text-blue-800 shadow-sm'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-blue-200'
                        }`}
                      >
                        <span className="font-medium">{tool.label}</span>
                        <input
                          type="checkbox"
                          checked={!!selectedTools[tool.id]}
                          onChange={() => toggleTool(tool.id)}
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                      </label>
                    ))}
                  </div>
                  <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-700">Team size</p>
                      <p className="text-xs text-slate-500">Pricing scales for 2+ users.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setSeatCount((prev) => Math.max(1, prev - 1))}
                        className="h-9 w-9 rounded-full border border-slate-200 text-lg font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-600"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min={1}
                        max={50}
                        value={seatCount}
                        onChange={(event) => setSeatCount(Number(event.target.value))}
                        className="h-10 w-16 rounded-lg border border-slate-200 text-center text-sm font-semibold text-slate-700 focus:border-blue-500 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setSeatCount((prev) => Math.min(50, prev + 1))}
                        className="h-9 w-9 rounded-full border border-slate-200 text-lg font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-600"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
                <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-cyan-50 p-5 text-left">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-500">Your estimate</p>
                  <div className="mt-4 space-y-3">
                    <div>
                      <p className="text-sm text-slate-500">Current spend (monthly)</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(currentMonthlySpend)}
                      </p>
                    </div>
                    <div className="rounded-xl border border-blue-200 bg-white px-4 py-3">
                      <p className="text-xs font-semibold uppercase text-blue-500">Recommended</p>
                      <p className="text-lg font-semibold text-slate-900">
                        {recommendationLabel} Explanatum ({planLabel})
                      </p>
                      <p className="text-sm text-slate-500">
                        {recommendAnnual
                          ? `${formatCurrency(annualPlanCost, 0)} / year`
                          : `${formatCurrency(monthlyPlanCost, 0)} / month`}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Potential savings</p>
                      <p className="text-3xl font-bold text-emerald-600">
                        {recommendAnnual
                          ? `${formatCurrency(recommendedSavings, 0)} / year`
                          : `${formatCurrency(recommendedSavings, 0)} / month`}
                      </p>
                      <p className="text-xs text-slate-500">
                        {recommendAnnual
                          ? `${formatCurrency(annualSavings, 0)} annually vs ${formatCurrency(monthlySavings, 0)} monthly`
                          : `${formatCurrency(monthlySavings, 0)} monthly vs ${formatCurrency(annualSavings, 0)} annually`}
                      </p>
                    </div>
                    <Link
                      to="/signup"
                      className="inline-flex w-full items-center justify-center rounded-xl bg-blue-600 px-4 py-3 text-base font-semibold text-white shadow-lg transition hover:bg-blue-700"
                    >
                      Start saving with {recommendationLabel}
                    </Link>
                    <p className="text-xs text-slate-500">
                      Estimates are illustrative. Excludes tools we don’t replace (e.g. accounting, design).
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* CTA Buttons - Xero style */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
            >
              Start Free — Build Smarter
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-blue-600 bg-white border-2 border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
            >
              See All 23 AI Modules
            </Link>
          </div>
          
          {/* Trust indicators */}
          <p className="mt-6 text-sm text-gray-500">
            Join thousands of businesses • 7-day free trial • Cancel anytime
          </p>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
